import { ethers } from 'ethers';
import { Hounds } from '../../../common/ethers-v5/Hounds';
import { Arenas } from '../../../common/ethers-v5/Arenas';
import { Queues } from '../../../common/ethers-v5/Queues';
import { Races } from '../../../common/ethers-v5/Races';
import { IERC20 } from '../../../common/ethers-v5/IERC20';
import { Hounds__factory } from '../../../common/ethers-v5/factories/Hounds__factory';
import { Arenas__factory } from '../../../common/ethers-v5/factories/Arenas__factory';
import { Queues__factory } from '../../../common/ethers-v5/factories/Queues__factory';
import { Races__factory } from '../../../common/ethers-v5/factories/Races__factory';
import { IERC20__factory } from '../../../common/ethers-v5/factories/IERC20__factory';

import { contracts, ContractsInventory, networkFactory, Network, currencies } from '../../../common/inventories';
import { Subject } from '../../../common/utils';
import { Ref, ref } from 'vue';

import { config } from '../config';
import {
  breedingPlatformFee,
  breedingTransactionCost,
  breedingSeasonActive,
  breedingCurrency,
  externalBreedingFeeCurrency,
  breedingCooldownCurrency,
  breedingCooldown,
  breedingCooldownTimeUnit,
  refillBreedingCooldownCost,
  staminaRefillCurrency,
  staminaRefill1x,
  staminaPerTimeUnit,
  staminaCap,
  breedingTransactionCostCurrency
} from '@/data/global';

export class EthReactiveProps {
  userAddress: string;
  chainId: number;
  balance: string;
  network: Network;
  initialized: boolean;
  unstoppableDomain: string;

  constructor(props: Partial<EthReactiveProps> = {}) {
    this.userAddress = props.userAddress || '';
    this.chainId = props.chainId || 0;
    this.balance = props.balance || '0';
    this.network = props.network;
    this.initialized = props.initialized || false;
    this.unstoppableDomain = props.unstoppableDomain || '';
  }
}

export class EthersService {
  static connection: any;
  static provider: ethers.providers.Web3Provider;
  static signer: ethers.providers.JsonRpcSigner;
  static balance: string;
  static userAddress: string;
  static chainId: number;
  static utils: typeof ethers.utils;
  static contractAddresses: ContractsInventory;
  static hounds: Hounds;
  static arenas: Arenas;
  static queues: Queues;
  static races: Races;

  static reactive: Ref<EthReactiveProps> = ref(new EthReactiveProps());
  static currenciesInfo;
  static supportedNetworks: Array<number> = [
    4126084, // alpha dune sepolia
  ];

  static connectionSubject = new Subject();

  static initialized: boolean = false;

  constructor() {}

  static async connect() {
    EthersService.initialized = false;
    EthersService.reactive.value.initialized = EthersService.initialized;
    EthersService.connection = (window as any).ethereum;
    EthersService.provider = new ethers.providers.Web3Provider(EthersService.connection, 'any');
    await EthersService.provider.send("eth_requestAccounts", []);
    EthersService.signer = EthersService.provider.getSigner();
    EthersService.userAddress = await EthersService.signer.getAddress();
    EthersService.chainId = await EthersService.signer.getChainId();
    EthersService.contractAddresses = contracts(config.dev ? -EthersService.chainId : EthersService.chainId);
    EthersService.utils = ethers.utils;
    EthersService.balance = EthersService.utils.formatEther(await EthersService.provider.getBalance(EthersService.userAddress));


    EthersService.hounds = Hounds__factory.connect(EthersService.contractAddresses.HOUNDS, EthersService.signer);
    EthersService.arenas = Arenas__factory.connect(EthersService.contractAddresses.ARENAS, EthersService.signer);
    EthersService.queues = Queues__factory.connect(EthersService.contractAddresses.QUEUES, EthersService.signer);
    EthersService.races = Races__factory.connect(EthersService.contractAddresses.RACES, EthersService.signer);

    EthersService.reactive.value.userAddress = EthersService.userAddress;
    EthersService.reactive.value.chainId = EthersService.chainId;
    EthersService.reactive.value.balance = EthersService.balance;
    EthersService.reactive.value.network = networkFactory(EthersService.chainId);

    if(!EthersService.initialized) {
      EthersService.initialized = true;
      EthersService.reactive.value.initialized = EthersService.initialized;
      EthersService.listenToMetamaskEvents();
      console.log('Ethers initialized: ');
      console.log({
        ...config,
        ...EthersService
      });
    }

    EthersService.hounds.control().then(data => {
      const auxBreedCost = data.fees.platformBreedFee;
      const auxTransactionCost = data.fees.breedTransactionFee;
      breedingPlatformFee.value = EthersService.utils.formatEther(auxBreedCost);
      breedingTransactionCost.value = EthersService.utils.formatEther(auxTransactionCost);
      breedingTransactionCostCurrency.value = data.fees.breedTransactionFeeCurrency;
      breedingCurrency.value = data.fees.platformBreedFeeCurrency;

      externalBreedingFeeCurrency.value = data.breeding.externalBreedingFeeCurrency;
      breedingCooldownCurrency.value = data.breeding.breedingCooldownCurrency;
      breedingCooldown.value = data.breeding.breedingCooldown.toNumber();
      breedingCooldownTimeUnit.value = data.breeding.breedingCooldownTimeUnit.toNumber();
      refillBreedingCooldownCost.value = data.breeding.refillBreedingCooldownCost.toNumber();

      staminaRefillCurrency.value = data.stamina.staminaRefillCurrency;
      staminaRefill1x.value = data.stamina.staminaRefill1x.toNumber();
      staminaPerTimeUnit.value = data.stamina.staminaPerTimeUnit;
      staminaCap.value = data.stamina.staminaCap;
    });

    breedingSeasonActive.value = await EthersService.hounds.matingSeason();

    EthersService.currenciesInfo = currencies(this.chainId);

    EthersService.connectionSubject.next();
  }

  static listenToMetamaskEvents() {
    EthersService.connection.on('accountsChanged', EthersService.connect);
    EthersService.connection.on('connect', EthersService.connect);

    EthersService.provider.on("network", (newNetwork, oldNetwork) => {
      // When a Provider makes its initial connection, it emits a "network"
      // event with a null oldNetwork along with the newNetwork. So, if the
      // oldNetwork exists, it represents a changing network
      if (oldNetwork) {
          window.location.reload();
      }
    });
  }

  static getCustomTokenInterface(currencyAddress: string): IERC20 {
    return IERC20__factory.connect(currencyAddress, EthersService.signer);
  }

  static async changeNetwork(network: Network) {
    try {
      await EthersService.connection.request({
        method: 'wallet_switchEthereumChain',
        params: [{
            chainId: network.chainId,
        }]
      });
    } catch (switchError) {
      // This error code indicates that the chain has not been added to MetaMask.
      if (switchError.code === 4902) {
        try {
          await EthersService.connection.request({
            method: "wallet_addEthereumChain",
            params: [{
                chainId: network.chainId,
                rpcUrls: network.rpcUrls,
                chainName: network.chainName,
                nativeCurrency: network.nativeCurrency,
                blockExplorerUrls: network.blockExplorerUrls
            }]
          });
        } catch (addError) {
          console.log(addError);
        }
      }
    }
    
  }

};